import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useTranslation } from 'react-i18next'
import { Plus, Printer, ShoppingBag, Trash2, X } from 'lucide-react'

import { useReservation } from '@/components/ReservationContext'
import { InvoiceModal } from '@/features/invoice/ui/InvoiceModal'
import { useHotelPricingConfig } from '@/shared/hooks/use-hotel-pricing-config'
import { ReservationBadge } from '@/entities/reservation/ui/ReservationBadge'
import { Button } from '@/shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { ConfirmationDialog } from '@/shared/ui/confirmation-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import {
    type Extra,
    type HairSalonReservation,
    type HotelReservation,
    ReservationStatus,
} from '@monorepo/functions/src/types/reservations'
import { type AdditionalService } from '@monorepo/functions/src/types/services'

import { ClientCard } from './ClientCard'
import { DatesCard } from './DatesCard'
import { PetsAndServicesCard } from './PetsAndServicesCard'
import { PriceBreakdownCard } from './PriceBreakdownCard'
import { SendHotelEmailDropdown } from './SendHotelEmailDropdown'

interface ReservationViewerProps {
    reservation: HairSalonReservation | HotelReservation
    isOpen: boolean
    onClose: () => void
}

export function HotelReservationViewer({ reservation: reservationParam, isOpen, onClose }: ReservationViewerProps) {
    const { t } = useTranslation()
    const { reservation, updateReservation } = useReservation(reservationParam.id)
    const { config: hotelPricingConfig } = useHotelPricingConfig()
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
    const [isTicketModalOpen, setIsTicketModalOpen] = useState(false)
    const navigate = useNavigate()

    console.log('reservation--------------------------------', reservation)

    const handleServiceChange = useCallback(
        (services: AdditionalService[]) => {
            console.log('handleServiceChange--------------------------------', services)
            updateReservation({ additionalServices: services })
        },
        [updateReservation],
    )

    const handleGoToShop = () => {
        navigate('/panel-interno/tienda')
        onClose()
    }

    const handleExtrasChange = (extras: Extra[]) => {
        if (reservation?.type !== 'hotel') return

        updateReservation({
            extras,
        })
    }

    const handleDeleteReservation = () => {
        updateReservation({ status: 'cancelled' as ReservationStatus })
        setIsDeleteDialogOpen(false)
    }

    if (!isOpen) return null
    if (!reservation) {
        return (
            <div className='fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l bg-background shadow-xl'>
                <div className='flex items-center justify-between border-b px-6 py-4'>
                    <div className='flex w-full items-center gap-2'>
                        <div className='flex w-full flex-col gap-2'>
                            <div className='h-6 w-1/3 animate-pulse rounded bg-muted' />
                            <div className='mt-1 flex gap-3'>
                                <div className='h-4 w-16 animate-pulse rounded bg-muted' />
                                <div className='h-4 w-4 animate-pulse rounded-full bg-muted' />
                                <div className='h-4 w-32 animate-pulse rounded bg-muted' />
                            </div>
                        </div>
                    </div>
                    <div className='h-8 w-8 animate-pulse rounded-full bg-muted' />
                </div>
                <div className='flex-1 space-y-8 overflow-y-auto p-6'>
                    <div className='flex flex-row gap-4'>
                        <div className='h-32 w-1/2 animate-pulse rounded bg-muted' />
                        <div className='h-32 w-1/2 animate-pulse rounded bg-muted' />
                    </div>
                    <div className='h-24 w-full animate-pulse rounded bg-muted' />
                    <div className='h-24 w-full animate-pulse rounded bg-muted' />
                    <div className='h-32 w-full animate-pulse rounded bg-muted' />
                </div>
            </div>
        )
    }

    const renderShopProducts = () => {
        if (!('shopProducts' in reservation) || !reservation.shopProducts?.length) return null

        return (
            <Card>
                <CardHeader>
                    <CardTitle className='flex items-center justify-between text-base font-medium'>
                        <div className='flex items-center gap-2'>
                            <ShoppingBag className='h-4 w-4' />
                            Productos de Tienda
                        </div>
                        <Button variant='outline' size='sm' onClick={handleGoToShop}>
                            <Plus className='mr-2 h-4 w-4' />
                            Ir a la Tienda
                        </Button>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className='w-[40%]'>Producto</TableHead>
                                <TableHead className='w-[15%] text-right'>Cantidad</TableHead>
                                <TableHead className='w-[20%] text-right'>Precio Unitario</TableHead>
                                <TableHead className='w-[15%] text-right'>Total</TableHead>
                                <TableHead className='w-[10%]'></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {'shopProducts' in reservation &&
                                reservation.shopProducts?.map((product, productIndex) => (
                                    <TableRow key={`${product.id}-${productIndex}`}>
                                        <TableCell className='align-middle'>
                                            <span className='font-medium'>{product.name}</span>
                                        </TableCell>
                                        <TableCell className='text-right align-middle'>{product.quantity}</TableCell>
                                        <TableCell className='text-right align-middle'>
                                            €{product.unitPrice.toFixed(2)}
                                        </TableCell>
                                        <TableCell className='text-right align-middle'>
                                            €{product.totalPrice.toFixed(2)}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant='destructive'
                                                size='sm'
                                                onClick={() => {
                                                    const newProducts = reservation.shopProducts?.filter(
                                                        (_, index) => index !== productIndex,
                                                    )
                                                    updateReservation({
                                                        shopProducts: newProducts,
                                                    })
                                                }}
                                            >
                                                Eliminar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        )
    }

    return (
        <div className='fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l bg-background shadow-xl'>
            <div className='flex items-center justify-between border-b px-6 py-4'>
                <div className='flex items-center gap-2'>
                    <div>
                        <h2 className='flex items-center gap-2 text-lg font-semibold'>
                            {reservation.type === 'hotel'
                                ? reservation.status === 'budget'
                                    ? 'Presupuesto de Hotel'
                                    : 'Reserva de Hotel'
                                : 'Reserva de Peluquería'}
                        </h2>
                        <div className='mt-1 flex items-center gap-3 text-sm text-muted-foreground'>
                            <ReservationBadge reservationStatus={reservation.status} />
                            {reservation.type === 'hotel' && (
                                <>
                                    <span>
                                        {reservation.pets.length}{' '}
                                        {reservation.pets.length === 1 ? 'mascota' : 'mascotas'}
                                    </span>
                                    <span>•</span>
                                    <span>
                                        {new Date(reservation.checkInDate).toLocaleDateString('es-ES')} -{' '}
                                        {new Date(reservation.checkOutDate).toLocaleDateString('es-ES')}
                                    </span>
                                    <Button
                                        variant='destructive'
                                        size='sm'
                                        onClick={() => setIsDeleteDialogOpen(true)}
                                        className='ml-2'
                                        disabled={reservation.status === 'cancelled'}
                                    >
                                        <Trash2 className='mr-1 h-4 w-4' />
                                        Eliminar
                                    </Button>
                                </>
                            )}
                            {reservation.type === 'peluqueria' && (
                                <>
                                    <span>1 mascota</span>
                                    <span>•</span>
                                    <span>
                                        {new Date(reservation.date).toLocaleDateString('es-ES')} {reservation.time}
                                    </span>
                                    <Button
                                        variant='destructive'
                                        size='sm'
                                        onClick={() => setIsDeleteDialogOpen(true)}
                                        className='ml-2'
                                        disabled={reservation.status === 'cancelled'}
                                    >
                                        <Trash2 className='mr-1 h-4 w-4' />
                                        Eliminar
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className='flex items-center gap-2'>
                    {reservation.type === 'hotel' && (
                        <>
                            <SendHotelEmailDropdown reservationId={reservation.id} />
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => setIsTicketModalOpen(true)}
                                className='gap-2'
                            >
                                <Printer className='h-4 w-4' />
                                {t('reservation.viewer.printTicket', 'Imprimir ticket')}
                            </Button>
                        </>
                    )}
                    <Button variant='ghost' size='icon' onClick={onClose}>
                        <X className='h-5 w-5' />
                    </Button>
                </div>
            </div>

            <div className='flex-1 space-y-8 overflow-y-auto p-6'>
                <div className='flex flex-row gap-4'>
                    <ClientCard client={reservation.client} onClientChange={client => updateReservation({ client })} />

                    {reservation.type === 'hotel' && (
                        <DatesCard reservation={reservation} onDatesChange={dates => updateReservation({ ...dates })} />
                    )}
                </div>

                {reservation.type === 'hotel' && (
                    <PetsAndServicesCard
                        reservation={reservation}
                        onPetsChange={pets => {
                            const updatedClientPets = [...reservation.client.pets]
                            const prevPets = reservation.pets
                            const sameSlotCount = pets.length === prevPets.length

                            pets.forEach((updatedPet, index) => {
                                const prevPet = prevPets[index]
                                let clientPetIndex = -1

                                if (sameSlotCount && prevPet?.name?.trim()) {
                                    clientPetIndex = updatedClientPets.findIndex(p => p.name === prevPet.name)
                                }

                                if (clientPetIndex === -1 && updatedPet.id?.trim()) {
                                    clientPetIndex = updatedClientPets.findIndex(p => p.id === updatedPet.id)
                                }

                                if (clientPetIndex === -1 && sameSlotCount && index < updatedClientPets.length) {
                                    clientPetIndex = index
                                }

                                if (clientPetIndex !== -1) {
                                    updatedClientPets[clientPetIndex] = {
                                        ...updatedClientPets[clientPetIndex],
                                        ...updatedPet,
                                    }
                                } else if (updatedPet.name?.trim() || updatedPet.breed?.trim()) {
                                    updatedClientPets.push({ ...updatedPet })
                                }
                            })

                            updateReservation({
                                client: {
                                    ...reservation.client,
                                    pets: updatedClientPets,
                                },
                                pets,
                            })
                        }}
                        handleServiceChange={handleServiceChange}
                        clientId={reservation.clientId}
                    />
                )}

                {renderShopProducts()}

                <PriceBreakdownCard
                    reservation={reservation as HotelReservation}
                    onExtrasChange={handleExtrasChange}
                    onServicesChange={handleServiceChange}
                    onShopProductsChange={shopProducts => updateReservation({ shopProducts })}
                    onGoToShop={handleGoToShop}
                />
            </div>

            <ConfirmationDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDeleteReservation}
                title='Eliminar reserva'
                description='¿Estás seguro de que quieres eliminar esta reserva? La reserva se marcará como cancelada.'
                confirmText='Eliminar'
                cancelText='Cancelar'
                variant='destructive'
            />

            {reservation.type === 'hotel' && isTicketModalOpen && (
                <InvoiceModal
                    reservation={reservation}
                    onClose={() => setIsTicketModalOpen(false)}
                    onPaid={() => {}}
                    clientClassification={reservation.client?.classification}
                    config={hotelPricingConfig}
                    printOnly
                />
            )}
        </div>
    )
}
